/**
 * Image caching and loading utilities with throttling and error recovery
 */

import { sanitizeUrl, addCacheBuster } from './url-helpers';

interface ImageLoadOptions {
  retries?: number;
  timeout?: number;
  fallbackUrl?: string;
}

interface QueuedLoad {
  url: string;
  options: ImageLoadOptions;
  resolve: (result: { success: boolean; url?: string; error?: string }) => void;
  reject: (error: Error) => void;
}

class ImageCache {
  private cache = new Map<string, Promise<{ success: boolean; url?: string; error?: string }>>();
  private loadQueue: QueuedLoad[] = [];
  private activeLoads = 0;
  private readonly maxConcurrentLoads = 3;
  private readonly baseDelay = 1000;

  private processQueue() {
    while (this.activeLoads < this.maxConcurrentLoads && this.loadQueue.length > 0) {
      const queuedLoad = this.loadQueue.shift();
      if (queuedLoad) {
        this.activeLoads++;
        this.loadImageWithRetry(queuedLoad.url, queuedLoad.options)
          .then(queuedLoad.resolve)
          .catch(queuedLoad.reject)
          .finally(() => {
            this.activeLoads--;
            this.processQueue();
          });
      }
    }
  }

  private async loadImageWithRetry(url: string, options: ImageLoadOptions): Promise<{ success: boolean; url?: string; error?: string }> {
    const { retries = 3, timeout = 10000, fallbackUrl } = options;
    let lastError = '';

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const sanitizedUrl = sanitizeUrl(url);
        const result = await this.loadSingleImage(sanitizedUrl, timeout);
        
        if (result.success) {
          return result;
        }
        
        lastError = result.error || 'Unknown error';
        
        // If this was a resource exhaustion error, wait longer
        if (lastError.includes('ERR_INSUFFICIENT_RESOURCES') && attempt < retries) {
          const delay = this.baseDelay * Math.pow(2, attempt);
          console.warn(`Resource exhaustion detected, waiting ${delay}ms before retry ${attempt + 1}`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Network error';
        
        if (attempt < retries) {
          const delay = this.baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // Try fallback URL if provided
    if (fallbackUrl && fallbackUrl !== url) {
      console.warn(`Primary image failed, trying fallback: ${fallbackUrl}`);
      try {
        const fallbackResult = await this.loadSingleImage(sanitizeUrl(fallbackUrl), timeout);
        if (fallbackResult.success) {
          return fallbackResult;
        }
      } catch (error) {
        console.warn('Fallback image also failed:', error);
      }
    }

    return { success: false, error: lastError };
  }

  private loadSingleImage(url: string, timeout: number): Promise<{ success: boolean; url?: string; error?: string }> {
    return new Promise((resolve) => {
      const img = new Image();
      const timeoutId = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        resolve({ success: false, error: 'Timeout' });
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        resolve({ success: true, url });
      };

      img.onerror = (error) => {
        clearTimeout(timeoutId);
        console.warn(`Failed to load image: ${url}`, error);
        resolve({ success: false, error: 'Load failed' });
      };

      img.src = url;
    });
  }

  async loadImage(url: string, options: ImageLoadOptions = {}): Promise<{ success: boolean; url?: string; error?: string }> {
    if (!url) {
      return { success: false, error: 'No URL provided' };
    }

    const cacheKey = `${url}_${JSON.stringify(options)}`;
    
    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Create promise for this load
    const loadPromise = new Promise<{ success: boolean; url?: string; error?: string }>((resolve, reject) => {
      this.loadQueue.push({ url, options, resolve, reject });
      this.processQueue();
    });

    // Cache the promise
    this.cache.set(cacheKey, loadPromise);

    return loadPromise;
  }

  clearCache() {
    this.cache.clear();
  }

  getCacheSize() {
    return this.cache.size;
  }
}

// Singleton instance
export const imageCache = new ImageCache();

// Helper function for components
export const loadCachedImage = (url: string, options: ImageLoadOptions = {}) => {
  return imageCache.loadImage(url, options);
};
